import { useState, useEffect } from 'react';
import { Settings, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface TimeBlock {
  start: string | null;
  end: string | null;
  enabled: boolean;
}

interface DaySchedule {
  block1: TimeBlock;
  block2: TimeBlock;
  enabled: boolean;
}

interface WorkingHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
}

const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { enabled: true, block1: { start: null, end: null, enabled: false }, block2: { start: '15:30', end: '19:30', enabled: true } },
  tuesday: { enabled: true, block1: { start: '10:00', end: '12:30', enabled: true }, block2: { start: '15:30', end: '19:30', enabled: true } },
  wednesday: { enabled: true, block1: { start: '10:00', end: '12:30', enabled: true }, block2: { start: '15:30', end: '19:30', enabled: true } },
  thursday: { enabled: true, block1: { start: '10:00', end: '12:30', enabled: true }, block2: { start: '15:30', end: '19:30', enabled: true } },
  friday: { enabled: true, block1: { start: '10:00', end: '12:30', enabled: true }, block2: { start: '15:30', end: '16:30', enabled: true } },
  saturday: { enabled: true, block1: { start: '10:00', end: '12:30', enabled: true }, block2: { start: '14:30', end: '16:30', enabled: true } },
};

const DAY_LABELS: Record<keyof WorkingHours, string> = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
};

export const D2DUserSettings = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [autoNaEnabled, setAutoNaEnabled] = useState(true);
  const [autoFollowupEnabled, setAutoFollowupEnabled] = useState(true);
  const [workingHours, setWorkingHours] = useState<WorkingHours>(DEFAULT_WORKING_HOURS);
  const [loading, setLoading] = useState(false);
  const [loadingFollowup, setLoadingFollowup] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [workingHoursExpanded, setWorkingHoursExpanded] = useState(true);

  // Load user settings on mount
  useEffect(() => {
    if (!user?.id) return;

    const loadSettings = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('auto_na_tagging_enabled, auto_followup_enabled, working_hours')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error loading settings:', error);
        return;
      }

      if (data?.auto_na_tagging_enabled !== null) {
        setAutoNaEnabled(data.auto_na_tagging_enabled);
      }

      if (data?.auto_followup_enabled !== null) {
        setAutoFollowupEnabled(data.auto_followup_enabled);
        // Collapse working hours by default if auto follow-up is disabled
        if (data.auto_followup_enabled === false) {
          setWorkingHoursExpanded(false);
        }
      }

      if (data?.working_hours) {
        setWorkingHours(data.working_hours as unknown as WorkingHours);
      }
    };

    loadSettings();
  }, [user?.id]);

  const handleAutoNaToggle = async (checked: boolean) => {
    if (!user?.id) return;

    setLoading(true);
    setAutoNaEnabled(checked);

    const { error } = await supabase
      .from('profiles')
      .update({ auto_na_tagging_enabled: checked })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating settings:', error);
      toast.error('Fehler beim Speichern der Einstellung');
      // Revert on error
      setAutoNaEnabled(!checked);
    } else {
      toast.success(
        checked 
          ? 'Auto-NA-Tagging aktiviert' 
          : 'Auto-NA-Tagging deaktiviert'
      );
    }

    setLoading(false);
  };

  const handleAutoFollowupToggle = async (checked: boolean) => {
    if (!user?.id) return;

    setLoadingFollowup(true);
    setAutoFollowupEnabled(checked);

    const { error } = await supabase
      .from('profiles')
      .update({ auto_followup_enabled: checked })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating settings:', error);
      toast.error('Fehler beim Speichern der Einstellung');
      setAutoFollowupEnabled(!checked);
    } else {
      toast.success(
        checked 
          ? 'Automatische Follow-ups aktiviert' 
          : 'Automatische Follow-ups deaktiviert'
      );
    }

    setLoadingFollowup(false);
  };

  const handleTimeChange = (
    day: keyof WorkingHours,
    block: 'block1' | 'block2',
    field: 'start' | 'end',
    value: string
  ) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [block]: {
          ...prev[day][block],
          [field]: value || null,
        },
      },
    }));
  };

  const handleDayToggle = (day: keyof WorkingHours, enabled: boolean) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled,
      },
    }));
  };

  const handleBlockToggle = (day: keyof WorkingHours, block: 'block1' | 'block2', enabled: boolean) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [block]: {
          ...prev[day][block],
          enabled,
        },
      },
    }));
  };

  const saveWorkingHours = async () => {
    if (!user?.id) return;

    setSavingHours(true);

    const { error } = await supabase
      .from('profiles')
      .update({ working_hours: JSON.parse(JSON.stringify(workingHours)) })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error saving working hours:', error);
      toast.error('Fehler beim Speichern der Arbeitszeiten');
    } else {
      toast.success('Arbeitszeiten gespeichert');
    }

    setSavingHours(false);
  };

  const resetToDefaults = () => {
    setWorkingHours(DEFAULT_WORKING_HOURS);
    toast.info('Auf Standardzeiten zurückgesetzt');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="mr-2"
          aria-label="Einstellungen"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Laufliste Einstellungen</DialogTitle>
          <DialogDescription>
            Passe deine persönlichen Einstellungen für die Laufliste an
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6 py-4">
            {/* Auto NA Tagging */}
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1 space-y-1">
                <Label htmlFor="auto-na-toggle" className="text-sm font-medium">
                  Automatisches NA-Tagging
                </Label>
                <p className="text-sm text-muted-foreground">
                  Units, die innerhalb von 1 Stunde nach Erstellung keinen Tag erhalten, werden automatisch als NA getaggt und bekommen einen Follow-up
                </p>
              </div>
              <Switch
                id="auto-na-toggle"
                checked={autoNaEnabled}
                onCheckedChange={handleAutoNaToggle}
                disabled={loading}
              />
            </div>

            <Separator />

            {/* Auto Follow-up */}
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1 space-y-1">
                <Label htmlFor="auto-followup-toggle" className="text-sm font-medium">
                  Automatische Follow-ups
                </Label>
                <p className="text-sm text-muted-foreground">
                  Bei NA-Tag wird automatisch ein Follow-up erstellt. Deaktivieren um manuell zu entscheiden.
                </p>
              </div>
              <Switch
                id="auto-followup-toggle"
                checked={autoFollowupEnabled}
                onCheckedChange={handleAutoFollowupToggle}
                disabled={loadingFollowup}
              />
            </div>

            <Separator />

            {/* Working Hours */}
            <Collapsible open={workingHoursExpanded} onOpenChange={setWorkingHoursExpanded}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full hover:bg-muted/50 rounded-md p-1 -ml-1 transition-colors">
                {workingHoursExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <Clock className="h-5 w-5 text-muted-foreground" />
                <Label className="text-sm font-medium cursor-pointer">Arbeitszeiten für Follow-ups</Label>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-3">
                <p className="text-sm text-muted-foreground">
                  Definiere deine Arbeitszeiten pro Wochentag. Follow-ups werden nur in diesen Zeiträumen geplant.
                </p>

                <div className="space-y-3">
                  {(Object.keys(DAY_LABELS) as Array<keyof WorkingHours>).map((day) => (
                    <div key={day} className="space-y-2 p-3 rounded-lg border border-border bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`day-${day}`}
                          checked={workingHours[day].enabled}
                          onCheckedChange={(checked) => handleDayToggle(day, checked === true)}
                        />
                        <Label htmlFor={`day-${day}`} className="text-sm font-medium cursor-pointer">
                          {DAY_LABELS[day]}
                        </Label>
                      </div>
                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${!workingHours[day].enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Block 1 - Vormittag */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`block1-${day}`}
                              checked={workingHours[day].block1.enabled}
                              onCheckedChange={(checked) => handleBlockToggle(day, 'block1', checked === true)}
                              disabled={!workingHours[day].enabled}
                            />
                            <Label htmlFor={`block1-${day}`} className="text-xs text-muted-foreground cursor-pointer">
                              Block 1 (Vormittag)
                            </Label>
                          </div>
                          <div className={`flex items-center gap-1 ${!workingHours[day].block1.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <Input
                              type="time"
                              value={workingHours[day].block1.start || ''}
                              onChange={(e) => handleTimeChange(day, 'block1', 'start', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="--:--"
                              disabled={!workingHours[day].enabled || !workingHours[day].block1.enabled}
                            />
                            <span className="text-muted-foreground">-</span>
                            <Input
                              type="time"
                              value={workingHours[day].block1.end || ''}
                              onChange={(e) => handleTimeChange(day, 'block1', 'end', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="--:--"
                              disabled={!workingHours[day].enabled || !workingHours[day].block1.enabled}
                            />
                          </div>
                        </div>
                        {/* Block 2 - Nachmittag */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`block2-${day}`}
                              checked={workingHours[day].block2.enabled}
                              onCheckedChange={(checked) => handleBlockToggle(day, 'block2', checked === true)}
                              disabled={!workingHours[day].enabled}
                            />
                            <Label htmlFor={`block2-${day}`} className="text-xs text-muted-foreground cursor-pointer">
                              Block 2 (Nachmittag)
                            </Label>
                          </div>
                          <div className={`flex items-center gap-1 ${!workingHours[day].block2.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <Input
                              type="time"
                              value={workingHours[day].block2.start || ''}
                              onChange={(e) => handleTimeChange(day, 'block2', 'start', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="--:--"
                              disabled={!workingHours[day].enabled || !workingHours[day].block2.enabled}
                            />
                            <span className="text-muted-foreground">-</span>
                            <Input
                              type="time"
                              value={workingHours[day].block2.end || ''}
                              onChange={(e) => handleTimeChange(day, 'block2', 'end', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="--:--"
                              disabled={!workingHours[day].enabled || !workingHours[day].block2.enabled}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetToDefaults}
                    className="flex-1"
                  >
                    Zurücksetzen
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveWorkingHours}
                    disabled={savingHours}
                    className="flex-1"
                  >
                    {savingHours ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
